/**
 * useGenerateAgentToken Hook
 *
 * React Query mutation hook for generating a new agent token.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dataAgentService } from '../../../api/data-agent.service';
import { DATA_AGENTS_QUERY_KEY } from './useDataAgents';
import { DATA_AGENT_DETAILS_QUERY_KEY } from './useDataAgentDetails';
import type { DataAgentGenerateTokenDTO, DataAgentTokenResponse } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseGenerateAgentTokenOptions {
  onSuccess?: (tokenResponse: DataAgentTokenResponse) => void;
  onError?: (error: string) => void;
}

export function useGenerateAgentToken(
  tenantId: string,
  dataAgentId: string,
  options?: UseGenerateAgentTokenOptions
) {
  const queryClient = useQueryClient();

  return useMutation<DataAgentTokenResponse, Error, DataAgentGenerateTokenDTO | undefined>({
    mutationFn: (data?: DataAgentGenerateTokenDTO) =>
      dataAgentService.generateToken(tenantId, dataAgentId, data),
    onSuccess: (tokenResponse) => {
      queryClient.invalidateQueries({ queryKey: [DATA_AGENTS_QUERY_KEY, tenantId] });
      queryClient.invalidateQueries({
        queryKey: [DATA_AGENT_DETAILS_QUERY_KEY, tenantId, dataAgentId],
      });
      options?.onSuccess?.(tokenResponse);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
